import { useEffect, useState } from 'react';
import { useRedline } from '@libs/redline';
import { IconAlertTriangle, IconMoney } from '@libs/atlas/components/icons/default';
import { legacyBase64Decrypt, legacyBase64Encrypt } from '@libs/utils/helpers/base64';
import { useOmniAds } from '@libs/utils/hooks/OmniAds';
import constants from '@common/constants';
import useApiPix from '@common/apis/pix/useApiPix';
import { partnersWithPixInternal, partnersWithPixExternal, partnersForTest, shouldGetPixWithoutBillet } from '../usePixExperiment';
import useGTM from '../useGTM';
import { statusLabelsProps, usePixProps, statusDictionaryProps, pixPaymentStatusTypes, handleQRCodeImageProps } from './types';

const usePix = ({
  installment,
  agreement,
  isMyAgreementsPage = false,
  debt,
  paymentOption,
  closedAgreement,
  refCopiedWarning,
  billetCode,
  isPollingBillet,
}: usePixProps) => {
  const { track: trackRedline } = useRedline();
  const { omniTracker } = useOmniAds();
  const GTM = useGTM();

  const { apiGetPixInternal, apiGeneratePixInternal, apiCheckPixPaymentStatus, apiGeneratePixExternal } = useApiPix(debt?.partner?.msUrl);

  const [pixData, setPixData] = useState<any>(closedAgreement?.pixData ? closedAgreement?.pixData : undefined);
  const [paymentStatus, setPaymentStatus] = useState<pixPaymentStatusTypes>(() => {
    const statusInLocalEncrypt = localStorage.getItem(constants.debts.PIX_STATUS);
    const statusDecrypt = statusInLocalEncrypt && !isMyAgreementsPage ? legacyBase64Decrypt(statusInLocalEncrypt) : '';

    return statusDecrypt ? statusDecrypt : 'pending';
  });

  const [isLoadingAgreementPix, setIsLoadingAgreementPix] = useState<boolean>(true);

  const idPartner = (agreement?.partner?.identificador || debt?.partner?.partnerIdentifier) ?? '';
  const systemSlug = debt?.partner?.identifier || agreement?.partner?.identificador || agreement?.creditor?.systemSlug || '';

  const isVivoPartner = idPartner === 'vivo';
  const isVivoCyber = isVivoPartner && systemSlug === 'vivocyber';
  const isVivoGlobalPixTest = isVivoPartner && systemSlug !== 'vivocyber' && !!closedAgreement?.flowWithPix;
  
  const [isPixExternal] = useState(() => partnersWithPixExternal?.includes(idPartner) || idPartner === 'lojasrenner');
  const [isLoadingCAPix, setIsLoadingCAPix] = useState<boolean>(shouldLoadPix());

  const [isPixTesting] = useState(() => partnersForTest?.includes(idPartner));
  const [isPixInternal] = useState(() => partnersWithPixInternal?.includes(idPartner) || idPartner === 'ativossa');
  const [singlePaymentMethod] = useState(() => constants.debts.PARTNERS_WITH_SINGLE_PAY_METHOD?.includes(idPartner));
  const [currentPaymentMethod] = useState(paymentOption?.paymentMethodName || agreement?.paymentMethod);

  //#region TODO: improve this stretch >>>>
  const [pixCode, setPixCode] = useState<string>(closedAgreement?.pixData?.data?.pix_code ? closedAgreement?.pixData?.data?.pix_code : '');
  const [qrCodeUrl, setQrCodeUrl] = useState<string>(() => {
    const { qrcode_base64, qrcode_link } = closedAgreement?.pixData?.data || {};
    return !!qrcode_base64 || !!qrcode_link ? handleQRCodeImage({ qrcode_base64, qrcode_link }) : '';
  });
  //#endregion <<<<<
  const copyPixButtonText = 'Copiar código do Pix';
  const [buttonText, setButtonText] = useState<string>(copyPixButtonText);

  const [isPixLoading, setIsPixLoading] = useState<boolean>(false);
  const [isPixSuccess, setIsPixSuccess] = useState<boolean>(false);

  const [isCodeCopied, setIsCodeCopied] = useState<boolean>(false);

  const isPixWithoutBillet = shouldGetPixWithoutBillet(idPartner);

  const statusDictionaryMyAgreements: statusDictionaryProps = {
    error: ['PIX_REFUNDED'],
    pending: ['PIX_PENDING_PAYMENT'],
    pixPaid: ['PIX_PAID', 'BILLET_PAID', 'BILLET_PAYMENT_ERROR', 'BILLET_REFUNDED', 'PIX_ERROR_REFUNDED'],
  };

  const statusDictionaryClosedAgreement: statusDictionaryProps = {
    error: ['BILLET_PAYMENT_ERROR', 'PIX_REFUNDED', 'PIX_ERROR_REFUNDED', 'BILLET_REFUNDED'],
    pending: ['PIX_PENDING_PAYMENT'],
    pixPaid: ['PIX_PAID', 'BILLET_PAID'],
  };

  const statusLabels: statusLabelsProps = {
    pending: {
      text: 'Aguardando pagamento',
      type: 'warning',
      icon: <></>,
    },
    pixPaid: {
      text: 'Pagamento em análise...',
      type: 'success',
      icon: !isMyAgreementsPage ? <></> : <IconMoney />,
    },
    inAnalysis: {
      text: 'Pagamento em análise...',
      type: 'success',
      icon: !isMyAgreementsPage ? <></> : <IconMoney />,
    },
    error: {
      text: 'Pagamento reembolsado',
      type: 'error',
      icon: <IconAlertTriangle />,
    },
  };

  const checkPixExpired = () => {
    const currentDueDate = new Date(installment?.dueDate).setHours(0, 0, 0, 0);
    const currentDate = new Date().setHours(0, 0, 0, 0);

    return currentDueDate < currentDate;
  };

  function getPixStatus(status: string, callBack: (status: pixPaymentStatusTypes) => void) {
    const statusDictionary = isMyAgreementsPage ? statusDictionaryMyAgreements : statusDictionaryClosedAgreement;

    Object.keys(statusDictionary).forEach((key: string) => {
      const validStatus = key as pixPaymentStatusTypes;

      const currentStatus = statusDictionary[validStatus];
      if (currentStatus.includes(status.toUpperCase() || status)) callBack(validStatus);
    });
  }

  const checkPaymentStatus = async () => {
    apiCheckPixPaymentStatus
      .send({ installmentCode: installment.installmentCode })
      .then((response) => {
        const statusApi = response.data.status || '';

        getPixStatus(statusApi, (status) => {
          localStorage.setItem(constants.debts.PIX_STATUS, legacyBase64Encrypt(status));

          setPaymentStatus(status);
        });
      })
      .catch((error) => {
        console.error('Error fetching payment status:', error);
      });
  };

  const pollingStatusPaymentPix = () => {
    if (pixCode !== '' && !isPixExternal) {
      const TEN_SECONDS_IN_MS = isMyAgreementsPage ? 10000 : 4000;
      const intervalStatusPix = setInterval(checkPaymentStatus, TEN_SECONDS_IN_MS);

      if (paymentStatus !== 'pending') {
        clearInterval(intervalStatusPix);
      }

      return () => clearInterval(intervalStatusPix);
    }
  };

  const handleCopyPixCode = () => {
    const redlineData = {
      agreementId: agreement?.id || closedAgreement?.agreement?.id,
      code: pixCode,
      outcomeType: 'COPY',
      paymentType: 'PIX',
      installmentId: String(installment?.id || agreement?.acordoParcelas?.[0]?.id),
    } as const;

    trackRedline.renegkit.paymentButtonClicked(redlineData);

    const trackingData = {
      code: pixCode,
      codeType: 'PIX',
      agreementId: agreement?.id || closedAgreement?.agreement?.id,
      debtId: agreement?.debt?.id || closedAgreement?.debt?.id,
      installmentId: installment.id,
      installment: installment?.installmentNumber || installment?.parcela,
      installmentValue: installment?.installmentValue || installment?.valorParcela,
    };

    const eventName = 'agreements.PaymentCodeCopied.v1'; // TODO Renegkit Validate omni
    const payloadOmni = {
      type: 'PIX',
      ...trackingData,
    };

    const msReturnDefaultButton = 2000;

    setIsPixLoading(true);

    navigator.clipboard
      .writeText(pixCode)
      .then(() => {
        trackRedline.renegkit.paymentCodeRetrieved(redlineData);

        omniTracker.all({
          google: {
            target: 'gALL',
            conversionId: 'AW-392945561/6rcWCK2ZpoIbEJm_r7sB',
            eventName: eventName,
            payload: {
              ...payloadOmni,
            },
          },
          meta: {
            eventName: 'Custom',
            payload: {
              eventName: eventName,
              ...payloadOmni,
            },
          },
        });

        setIsCodeCopied(true);

        setIsPixLoading(false);
        setIsPixSuccess(true);

        if (refCopiedWarning?.current) {
          refCopiedWarning.current.open({
            visible: true,
            typeCode: 'Pix',
          });
        }

        setButtonText('Código pix copiado');

        setTimeout(() => {
          setIsPixLoading(false);
          setIsPixSuccess(false);

          setIsCodeCopied(false);
          setButtonText(copyPixButtonText);

          if (refCopiedWarning?.current) {
            refCopiedWarning.current.open({
              visible: false,
            });
          }
        }, msReturnDefaultButton);
      })
      .catch((error) => {
        trackRedline.renegkit.paymentCodeRetrievalFailed({
          ...redlineData,
          error: error.message,
        });

        console.error('Erro ao copiar o código:', error);
      });
  };

  function shouldLoadPix() {
    const isClosedAgreement = !isMyAgreementsPage;
    const hasPixFlow = closedAgreement?.flowWithPix;
    const hasPixCode = !!closedAgreement?.pixData?.data?.pix_code;

    // acordo-fechado: sem fluxo Pix ou já tiver código Pix, não exibe loader do pix e não tenta gerar o código
    if ((isClosedAgreement && !hasPixFlow) || (isClosedAgreement && hasPixCode)) return false;

    // acordo-fechado: polling do boleto ativo, exibe loader do pix e tenta gerar o código
    if (isClosedAgreement && isPollingBillet) return true;

    // acordo-fechado: sem código do boleto, sem polling do boleto e não é pix externo, não exibe loader do pix e não tenta gerar o código
    if (isClosedAgreement && !billetCode && !isPollingBillet && !isPixExternal) return false;

    // nos demais casos, exibe loader do pix e tenta gerar o código
    return true;
  }

  const generatePixExternal = () => {
    const installmentCode = isMyAgreementsPage ? installment?.installmentCode : agreement?.acordoParcelas?.[0]?.installmentCode;

    const requestPix = {
      installmentCode: installmentCode,
      ...(isVivoGlobalPixTest && { origin: 'ac_vivo_pixleto_test' }),
    };

    const msUrl = agreement?.partner?.msUrl || debt?.partner?.msUrl;
    apiGeneratePixExternal
      .send(msUrl, requestPix)
      .then((response) => {
        const safeData = response.data || {};
        const { pix_code, qrcode_base64, qrcode_link } = safeData;
        setPixData(response);

        if (!isMyAgreementsPage) {
          // TODO: util, next chapter  <<<
          const closedAgreementFromLocal = localStorage.getItem(constants.debts.CLOSED_AGREEMENT) || '';
          const descryptClosedAgreement = JSON.parse(legacyBase64Decrypt(closedAgreementFromLocal));

          const updatedClosedAgreement = {
            ...descryptClosedAgreement,
            pixData: response,
          };
          const encryptClosedAgreement = legacyBase64Encrypt(JSON.stringify(updatedClosedAgreement));

          localStorage.setItem(constants.debts.CLOSED_AGREEMENT, encryptClosedAgreement);
        }

        if (pix_code) {
          const currDebt = isMyAgreementsPage ? agreement?.debt : debt;
          trackRedline.debts.pixGenerated({
            pixCode: pix_code,
            paymentOption,
            debt: currDebt,
            agreement: agreement,
            isExternalPix: isPixWithoutBillet ? false : true,
          });

          setPixCode(pix_code);
          setQrCodeUrl(handleQRCodeImage({ qrcode_base64, qrcode_link }));
        }
      })
      .catch((error) => {
        console.error('Error generating pix', error);

        // setPaymentStatus("errorRequest") TODO: Add new status for the user to try to generate the pix again?
        trackRedline.debts.pixGenerationErrored({
          errorDetails: error?.fields?.[0]?.field_name || 'Unknown details',
          errorMessage: error?.message || 'Unknown message',
          errorType: error?.error_slug || 'Unknown type',
          paymentOption,
          debt,
          agreement: agreement,
        });
      })
      .finally(() => {
        setIsLoadingCAPix(false);
        setIsLoadingAgreementPix(false);
      });
  };

  const generatePixInternal = () => {
    if (!billetCode) return;

    const agreementInstallments = agreement?.acordoParcelas?.[0];
    const isVivoCyberPixletoTest = closedAgreement?.flowWithPix && isVivoCyber;

    const requestPix = {
      barcode: billetCode,
      installment_id: agreementInstallments.id,
      payment_method: agreement?.paymentMethod,
      ...(isVivoCyberPixletoTest && { origin: 'ac_vivo_pixleto_test' }),
    };

    apiGeneratePixInternal
      .send(requestPix)
      .then((response) => {
        const safeData = response.data || {};
        const { pix_code, qrcode_link, qrcode_base64 } = safeData;
        setPixData(response);

        if (!isMyAgreementsPage) {
          // TODO: util, next chapter  <<<
          const closedAgreementFromLocal = localStorage.getItem(constants.debts.CLOSED_AGREEMENT) || '';
          const descryptClosedAgreement = JSON.parse(legacyBase64Decrypt(closedAgreementFromLocal));

          const updatedClosedAgreement = {
            ...descryptClosedAgreement,
            pixData: response,
          };
          const encryptClosedAgreement = legacyBase64Encrypt(JSON.stringify(updatedClosedAgreement));

          localStorage.setItem(constants.debts.CLOSED_AGREEMENT, encryptClosedAgreement);
        }

        if (pix_code) {
          const currDebt = isMyAgreementsPage ? agreement?.debt : debt;
          trackRedline.debts.pixGenerated({
            pixCode: pix_code,
            paymentOption,
            debt: currDebt,
            agreement: agreement,
          });

          GTM.trigger.pixGenerated();

          setPixCode(pix_code);
          setQrCodeUrl(handleQRCodeImage({ qrcode_base64, qrcode_link }));
        }
      })
      .catch((error) => {
        console.error('[ERROR GENERATING PIX]', error);
        setQrCodeUrl('');

        trackRedline.debts.pixGenerationErrored({
          errorDetails: error?.fields?.[0]?.field_name || 'Unknown details',
          errorMessage: error?.message || 'Unknown message',
          errorType: error?.error_slug || 'Unknown type',
          paymentOption,
          debt,
          agreement: agreement,
        });
      })
      .finally(() => {
        setIsLoadingCAPix(false);
        setIsLoadingAgreementPix(false);
      });
  };

  const getPixCodeInternal = () => {
    const { installmentCode = '' } = installment || {};

    const shouldGetInternalPix = isPixInternal || (isPixTesting && !isPixExternal);

    const isPixExpired = checkPixExpired();
    if (!shouldGetInternalPix || !billetCode || isPixExpired) return;

    apiGetPixInternal
      .send({ installmentCode })
      .then((response) => {
        const { pix_code, has_pix, status, qrcode_link, qrcode_base64 } = response.data;
        setPixData(response);

        if (!has_pix && !isPixExternal) return;

        setQrCodeUrl(handleQRCodeImage({ qrcode_base64, qrcode_link }));
        setPixCode(pix_code);

        if (!!status)
          getPixStatus(status, (status) => {
            return setPaymentStatus(status);
          });
      })
      .catch((error) => {
        console.error('[GET PIX CODE]:', error);
      })
      .finally(() => {
        setIsLoadingCAPix(false);
        setIsLoadingAgreementPix(false);
      });
  };

  const handleGeneratePixCode = () => {
    if (installment?.properties) {
      const pixAgreementProperty = installment.properties.find(
        (property: { key: string; stringValue: string }) => property.key === 'AGREEMENT_EXTERNAL_PIX_CODE'
      );

      if (pixAgreementProperty?.stringValue) {
        setPixCode(pixAgreementProperty.stringValue);
      }
    }

    if (agreement && agreement?.situation !== 'ACTIVE') return; // create a global enum for the statuses and situations of agreements

    const currentPaymentIsPix = currentPaymentMethod === 'PIX';

    //#region itau rule for pix
    const isItau = agreement?.partner?.identificador === 'itau';
    const isFirstInstallment = installment?.installmentNumber === 1;

    if (isItau && !isFirstInstallment && currentPaymentIsPix) return;
    //#endregion itau rule for pix

    const notReqPix = singlePaymentMethod && !currentPaymentIsPix;

    if (notReqPix || (pixData && !!qrCodeUrl) || !shouldLoadPix()) return;

    if (isPixExternal || isPixWithoutBillet || isVivoGlobalPixTest) {
      generatePixExternal();
    } else if (!isMyAgreementsPage && !isPixExternal) {
      generatePixInternal();
    } else {
      getPixCodeInternal();
    }
  };

  function handleQRCodeImage({ qrcode_base64, qrcode_link }: handleQRCodeImageProps) {
    if (!!qrcode_base64) return `data:image/png;base64,${qrcode_base64}`;

    return qrcode_link;
  }

  useEffect(() => handleGeneratePixCode(), [billetCode]);
  useEffect(() => pollingStatusPaymentPix(), [pixCode, paymentStatus]);

  return {
    pixData,
    paymentStatus,
    setPaymentStatus,
    pixCode,
    qrCodeUrl,
    isPixExternal,
    handleCopyPixCode,
    handleGeneratePixCode,
    buttonText,
    statusLabels,
    checkPaymentStatus,
    isPixLoading,
    isPixSuccess,
    isCodeCopied,
    statusDictionaryClosedAgreement,
    singlePaymentMethod,
    isLoadingAgreementPix,
    checkPixExpired,
    isLoadingCAPix,
    isPixWithoutBillet,
  };
};

export default usePix;
